/**
 * How the incident timeline names what a PSA integration did (docs/PsaIntegration.md,
 * decision 3): the integration is the actor, naming the PSA person when the PSA said
 * who — "by Jane Tech via Autotask", or "by Autotask". Read from the event's
 * `metadata.actor`, which the backend writes with `createdBy` null.
 */

interface IntegrationActor {
  kind: 'integration';
  name: string;
  person?: { name?: string | null } | null;
  /** The Witina user the PSA person is, by a verified email of an organization member. */
  witina_email?: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function integrationActor(metadata: unknown): IntegrationActor | null {
  if (!isRecord(metadata) || !isRecord(metadata.actor)) return null;
  const actor = metadata.actor;
  if (actor.kind !== 'integration' || typeof actor.name !== 'string') return null;
  return actor as unknown as IntegrationActor;
}

/**
 * "by Jane Tech (Witina: jane@msp.example) via Autotask", "by Jane Tech via Autotask",
 * "by Autotask", or null for anything else.
 */
export function integrationActorLabel(metadata: unknown): string | null {
  const actor = integrationActor(metadata);
  if (!actor) return null;
  const person = actor.person?.name?.trim();
  if (!person) return `by ${actor.name}`;
  const witina = actor.witina_email?.trim();
  return witina ? `by ${person} (Witina: ${witina}) via ${actor.name}` : `by ${person} via ${actor.name}`;
}

/**
 * A PSA note the end customer can see, or wrote, is labelled (decision 12). Null for
 * an internal note and for anything that isn't a PSA note.
 */
export function psaVisibilityLabel(metadata: unknown): string | null {
  if (!isRecord(metadata) || !isRecord(metadata.psa)) return null;
  if (metadata.psa.from_customer === true) return 'From the customer';
  if (metadata.psa.customer_visible === true) return 'Visible to the customer';
  return null;
}
